import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  Button,
  Stack,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Checkbox,
  IconButton,
  Chip,
  Input,
} from "@mui/joy";
import FolderIcon from "@mui/icons-material/Folder";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { SharepointFolderSelection } from "../../../common/types";
import {
  useSharepointLibraries,
  useSharepointFolders,
  DocumentLibrary,
  FolderItem,
} from "../../../services/sharepointService";

interface SharepointFolderPickerProps {
  open: boolean;
  workspaceId: string;
  sourceName: string;
  siteUrl: string;
  tenantId: string;
  clientId: string;
  clientSecret?: string;
  selectionId?: string;
  initialSelections?: SharepointFolderSelection[];
  onClose: () => void;
  onConfirm: (selections: SharepointFolderSelection[]) => void;
}

interface FolderNode extends FolderItem {
  drive_id: string;
  children?: FolderNode[];
}

interface LibrarySection {
  library: DocumentLibrary;
  roots: FolderNode[];
  loading: boolean;
  error: string | null;
}

export const getSharePointLibrariesErrorMessage = (err: any): string => {
  const detail = err?.response?.detail;
  const detailCode = typeof detail === "object" ? detail?.code : undefined;

  if (detailCode === "selection_disabled") {
    return (
      "Connection failed. This SharePoint connection is disabled. Re-enable it " +
      "from Workspace Settings → Integrations → SharePoint."
    );
  }

  let msg = "Connection failed. ";
  if (err.message?.includes("401") || err.message?.includes("Unauthorized")) {
    msg +=
      "Invalid credentials. Check your Tenant ID, Client ID, and Client Secret.";
  } else if (
    err.message?.includes("403") ||
    err.message?.includes("Forbidden")
  ) {
    msg +=
      'Permission denied. Ensure the app has "Sites.Read.All" with admin consent.';
  } else if (
    err.message?.includes("404") ||
    err.message?.includes("Not Found")
  ) {
    msg += "Site not found. Please verify the Site URL.";
  } else {
    msg += err.message || "Unknown error occurred.";
  }
  return msg;
};

const SharepointFolderPicker: React.FC<SharepointFolderPickerProps> = ({
  open,
  workspaceId,
  sourceName,
  siteUrl,
  tenantId,
  clientId,
  clientSecret,
  selectionId,
  initialSelections = [],
  onClose,
  onConfirm,
}) => {
  const [allLibraries, setAllLibraries] = useState<DocumentLibrary[]>([]);
  const [selectedLibraryIds, setSelectedLibraryIds] = useState<Set<string>>(
    new Set(),
  );
  const [librarySections, setLibrarySections] = useState<
    Map<string, LibrarySection>
  >(new Map());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [loadingNodes, setLoadingNodes] = useState<Set<string>>(new Set());
  const [selectedFolders, setSelectedFolders] = useState<
    Map<string, SharepointFolderSelection>
  >(new Map());
  const [searchQuery, setSearchQuery] = useState("");
  const [includeSubfolders, setIncludeSubfolders] = useState(true);
  const [librariesLoading, setLibrariesLoading] = useState(false);
  const [librariesError, setLibrariesError] = useState<string | null>(null);

  const { fetchLibraries } = useSharepointLibraries();
  const { fetchFolders } = useSharepointFolders();

  const wasOpenRef = useRef(false);
  const hasLoadedRef = useRef(false);

  // ---- Path helpers ----

  const normalizePathValue = useCallback((path?: string) => {
    if (!path) return "";
    return path
      .replace(/\\/g, "/")
      .replace(/^\/+|\/+$/g, "")
      .toLowerCase();
  }, []);

  const buildPathKey = useCallback(
    (path?: string) => {
      const n = normalizePathValue(path);
      return n ? `path:${n}` : "";
    },
    [normalizePathValue],
  );

  const splitPathParts = useCallback(
    (path?: string) =>
      normalizePathValue(path)
        .split("/")
        .filter((p) => p.length > 0),
    [normalizePathValue],
  );

  // ---- Selection helpers ----

  const findSelectionEntryForNode = useCallback(
    (node: FolderNode) => {
      if (selectedFolders.has(node.drive_item_id)) {
        return {
          key: node.drive_item_id,
          selection: selectedFolders.get(node.drive_item_id)!,
        };
      }
      const normalizedNodePath = normalizePathValue(node.path);
      if (!normalizedNodePath) return null;
      for (const [key, sel] of selectedFolders.entries()) {
        if (!sel.path) continue;
        if (normalizedNodePath === normalizePathValue(sel.path)) {
          return { key, selection: sel };
        }
      }
      return null;
    },
    [selectedFolders, normalizePathValue],
  );

  const getNodeSelectionState = useCallback(
    (node: FolderNode): { checked: boolean; indeterminate: boolean } => {
      if (findSelectionEntryForNode(node))
        return { checked: true, indeterminate: false };

      const nodeParts = splitPathParts(node.path);
      const hasSelectedDescendants =
        nodeParts.length > 0
          ? Array.from(selectedFolders.values()).some((sel) => {
              const selParts = splitPathParts(sel.path);
              return (
                selParts.length > nodeParts.length &&
                nodeParts.every((p, i) => selParts[i] === p)
              );
            })
          : false;

      if (!node.children?.length) {
        return { checked: false, indeterminate: hasSelectedDescendants };
      }

      let selectedCount = 0;
      let totalCount = 0;

      const countDescendants = (children: FolderNode[]) => {
        for (const child of children) {
          totalCount++;
          if (findSelectionEntryForNode(child)) selectedCount++;
          if (child.children?.length) countDescendants(child.children);
        }
      };
      countDescendants(node.children);

      if (selectedCount === 0)
        return { checked: false, indeterminate: hasSelectedDescendants };
      if (selectedCount === totalCount)
        return { checked: true, indeterminate: false };
      return { checked: false, indeterminate: true };
    },
    [findSelectionEntryForNode, selectedFolders, splitPathParts],
  );

  // ---- Tree update helper ----

  const updateTreeNodes = (
    nodes: FolderNode[],
    targetId: string,
    children: FolderNode[],
  ): FolderNode[] =>
    nodes.map((node) => {
      if (node.drive_item_id === targetId) return { ...node, children };
      if (node.children?.length)
        return {
          ...node,
          children: updateTreeNodes(node.children, targetId, children),
        };
      return node;
    });

  // ---- Data loading ----

  const loadRootFolders = useCallback(
    async (library: DocumentLibrary) => {
      setLibrarySections((prev) => {
        const next = new Map(prev);
        next.set(library.drive_id, {
          library,
          roots: [],
          loading: true,
          error: null,
        });
        return next;
      });

      try {
        const { folders } = await fetchFolders(
          workspaceId,
          library.drive_id,
          undefined,
          sourceName,
          tenantId,
          clientId,
          clientSecret,
          selectionId,
        );

        const roots: FolderNode[] = folders.map((f) => ({
          ...f,
          drive_id: library.drive_id,
          children: [],
        }));

        setLibrarySections((prev) => {
          const next = new Map(prev);
          next.set(library.drive_id, {
            library,
            roots,
            loading: false,
            error: null,
          });
          return next;
        });
      } catch (err: any) {
        setLibrarySections((prev) => {
          const next = new Map(prev);
          next.set(library.drive_id, {
            library,
            roots: [],
            loading: false,
            error: err.message || "Failed to load folders.",
          });
          return next;
        });
      }
    },
    [
      fetchFolders,
      workspaceId,
      sourceName,
      tenantId,
      clientId,
      clientSecret,
      selectionId,
    ],
  );

  const loadLibraries = useCallback(async () => {
    try {
      setLibrariesLoading(true);
      setLibrariesError(null);

      const libs = await fetchLibraries(
        workspaceId,
        siteUrl,
        sourceName,
        tenantId,
        clientId,
        clientSecret,
        selectionId,
      );

      setAllLibraries(libs);

      if (libs.length === 0) {
        setLibrariesError(
          "No document libraries found at this site. Please verify the Site URL is correct.",
        );
        return;
      }

      const allIds = new Set(libs.map((l) => l.drive_id));
      setSelectedLibraryIds(allIds);
      setExpandedSections(allIds);

      await Promise.all(libs.map((lib) => loadRootFolders(lib)));
    } catch (err: any) {
      setLibrariesError(getSharePointLibrariesErrorMessage(err));
    } finally {
      setLibrariesLoading(false);
    }
  }, [
    fetchLibraries,
    loadRootFolders,
    workspaceId,
    siteUrl,
    sourceName,
    tenantId,
    clientId,
    clientSecret,
    selectionId,
  ]);

  // Init selections on open
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      hasLoadedRef.current = false;
      const selectionsMap = new Map<string, SharepointFolderSelection>();
      initialSelections.forEach((sel, index) => {
        const fallbackKey = buildPathKey(sel.path) || `legacy-${index}`;
        const key = sel.drive_item_id || fallbackKey;
        if (key) selectionsMap.set(key, sel);
      });
      setSelectedFolders(selectionsMap);
      setSearchQuery("");
      setAllLibraries([]);
      setLibrarySections(new Map());
      setSelectedLibraryIds(new Set());
      setExpandedSections(new Set());
      setExpandedNodes(new Set());
    }
    wasOpenRef.current = open;
  }, [open, initialSelections, buildPathKey]);

  // Load libraries when modal opens
  useEffect(() => {
    const hasSavedCredentials = Boolean(sourceName || selectionId);
    const hasInlineCredentials = Boolean(tenantId && clientId && clientSecret);
    if (
      open &&
      workspaceId &&
      siteUrl &&
      (hasSavedCredentials || hasInlineCredentials) &&
      !hasLoadedRef.current
    ) {
      hasLoadedRef.current = true;
      loadLibraries();
    } else if (!open) {
      hasLoadedRef.current = false;
    }
  }, [
    open,
    workspaceId,
    siteUrl,
    sourceName,
    selectionId,
    tenantId,
    clientId,
    clientSecret,
    loadLibraries,
  ]);

  // ---- Interaction handlers ----

  const toggleLibrary = useCallback(
    (driveId: string) => {
      setSelectedLibraryIds((prev) => {
        const next = new Set(prev);
        if (next.has(driveId)) {
          next.delete(driveId);
          // Remove any selected folders belonging to this library
          const section = librarySections.get(driveId);
          if (section) {
            const libName = section.library.name;
            setSelectedFolders((prevFolders) => {
              const newMap = new Map(prevFolders);
              for (const [key, sel] of newMap.entries()) {
                if (sel.library_name === libName) newMap.delete(key);
              }
              return newMap;
            });
          }
        } else {
          next.add(driveId);
          setExpandedSections((prevExp) => new Set(prevExp).add(driveId));
          // Load folders if not yet loaded
          if (!librarySections.has(driveId)) {
            const lib = allLibraries.find((l) => l.drive_id === driveId);
            if (lib) loadRootFolders(lib);
          }
        }
        return next;
      });
    },
    [allLibraries, librarySections, loadRootFolders],
  );

  const loadChildFolders = useCallback(
    async (node: FolderNode) => {
      try {
        setLoadingNodes((prev) => new Set(prev).add(node.drive_item_id));

        const { folders } = await fetchFolders(
          workspaceId,
          node.drive_id,
          node.drive_item_id,
          sourceName,
          tenantId,
          clientId,
          clientSecret,
          selectionId,
        );

        const childNodes: FolderNode[] = folders.map((f) => ({
          ...f,
          drive_id: node.drive_id,
          children: [],
        }));

        setLibrarySections((prev) => {
          const next = new Map(prev);
          const section = next.get(node.drive_id);
          if (!section) return next;
          next.set(node.drive_id, {
            ...section,
            roots: updateTreeNodes(
              section.roots,
              node.drive_item_id,
              childNodes,
            ),
          });
          return next;
        });

        setExpandedNodes((prev) => new Set(prev).add(node.drive_item_id));
      } catch (err: any) {
        console.error("Failed to load child folders:", err);
      } finally {
        setLoadingNodes((prev) => {
          const next = new Set(prev);
          next.delete(node.drive_item_id);
          return next;
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fetchFolders,
      workspaceId,
      sourceName,
      tenantId,
      clientId,
      clientSecret,
      selectionId,
    ],
  );

  const handleToggleExpand = useCallback(
    async (node: FolderNode) => {
      if (!node.has_children) return;
      const isExpanded = expandedNodes.has(node.drive_item_id);
      if (isExpanded) {
        setExpandedNodes((prev) => {
          const next = new Set(prev);
          next.delete(node.drive_item_id);
          return next;
        });
      } else if (!node.children?.length) {
        await loadChildFolders(node);
      } else {
        setExpandedNodes((prev) => new Set(prev).add(node.drive_item_id));
      }
    },
    [expandedNodes, loadChildFolders],
  );

  const handleToggleSelect = useCallback(
    (node: FolderNode) => {
      const existing = findSelectionEntryForNode(node);
      if (existing) {
        setSelectedFolders((prev) => {
          const next = new Map(prev);
          next.delete(existing.key);
          return next;
        });
      } else {
        const library = allLibraries.find((l) => l.drive_id === node.drive_id);
        const sel: SharepointFolderSelection = {
          drive_item_id: node.drive_item_id,
          display_name: node.name,
          path: node.path,
          library_name: library?.name || "",
          recursive: includeSubfolders,
        };
        const key =
          node.drive_item_id || buildPathKey(node.path) || `path:${node.name}`;
        setSelectedFolders((prev) => new Map(prev).set(key, sel));
      }
    },
    [allLibraries, buildPathKey, findSelectionEntryForNode, includeSubfolders],
  );

  const handleRemoveSelection = (key: string) => {
    setSelectedFolders((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selectedFolders.values()));
    onClose();
  };

  // ---- Search filter ----

  const nodeMatchesSearch = (node: FolderNode, query: string): boolean => {
    if (node.name.toLowerCase().includes(query.toLowerCase())) return true;
    return node.children?.some((c) => nodeMatchesSearch(c, query)) ?? false;
  };

  const filterNodes = (nodes: FolderNode[], query: string): FolderNode[] => {
    if (!query) return nodes;
    return nodes
      .filter((n) => nodeMatchesSearch(n, query))
      .map((n) => ({
        ...n,
        children: n.children ? filterNodes(n.children, query) : [],
      }));
  };

  // ---- Render ----

  const renderFolderNode = (
    node: FolderNode,
    level: number = 0,
  ): React.ReactNode => {
    const isExpanded =
      expandedNodes.has(node.drive_item_id) || searchQuery.length > 0;
    const isNodeLoading = loadingNodes.has(node.drive_item_id);
    const { checked, indeterminate } = getNodeSelectionState(node);
    const highlight = checked || indeterminate;

    const visibleChildren = filterNodes(node.children ?? [], searchQuery);

    return (
      <Box key={node.drive_item_id}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            py: 0.4,
            px: 1,
            pl: level * 2.5 + 1,
            cursor: "pointer",
            borderRadius: "sm",
            backgroundColor: highlight ? "primary.softBg" : "transparent",
            "&:hover": {
              backgroundColor: highlight
                ? "primary.softHoverBg"
                : "neutral.softHoverBg",
            },
          }}
        >
          <IconButton
            size="sm"
            variant="plain"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleExpand(node);
            }}
            disabled={!node.has_children || isNodeLoading}
            sx={{ minWidth: 20, minHeight: 20, p: 0, mr: 0.5 }}
          >
            {isNodeLoading ? (
              <CircularProgress size="sm" thickness={2} />
            ) : node.has_children ? (
              isExpanded ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )
            ) : (
              <Box sx={{ width: 16 }} />
            )}
          </IconButton>

          <Checkbox
            size="sm"
            checked={checked}
            indeterminate={indeterminate}
            onChange={() => handleToggleSelect(node)}
            sx={{ mr: 0.75 }}
          />

          <FolderIcon
            sx={{
              fontSize: 18,
              mr: 0.75,
              color: highlight ? "primary.500" : "neutral.400",
            }}
          />

          <Typography
            level="body-sm"
            fontWeight={highlight ? "md" : "normal"}
            sx={{ flex: 1 }}
          >
            {node.name}
          </Typography>
        </Box>

        {isExpanded && visibleChildren.length > 0 && (
          <Box>
            {visibleChildren.map((child) => renderFolderNode(child, level + 1))}
          </Box>
        )}
      </Box>
    );
  };

  const renderLibrarySection = (section: LibrarySection): React.ReactNode => {
    const { library, roots, loading, error } = section;
    const isExpanded =
      expandedSections.has(library.drive_id) || searchQuery.length > 0;
    const visibleRoots = filterNodes(roots, searchQuery);

    return (
      <Box key={library.drive_id}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            py: 0.6,
            px: 1,
            cursor: "pointer",
            backgroundColor: "background.level1",
            borderRadius: "sm",
            "&:hover": { backgroundColor: "neutral.softHoverBg" },
          }}
          onClick={() =>
            setExpandedSections((prev) => {
              const next = new Set(prev);
              if (next.has(library.drive_id)) next.delete(library.drive_id);
              else next.add(library.drive_id);
              return next;
            })
          }
        >
          {isExpanded ? (
            <ExpandMoreIcon
              fontSize="small"
              sx={{ mr: 0.75, color: "neutral.500", flexShrink: 0 }}
            />
          ) : (
            <ChevronRightIcon
              fontSize="small"
              sx={{ mr: 0.75, color: "neutral.500", flexShrink: 0 }}
            />
          )}
          <FolderIcon
            sx={{ fontSize: 18, mr: 0.75, color: "primary.600", flexShrink: 0 }}
          />
          <Typography level="body-sm" fontWeight="lg" sx={{ flex: 1 }}>
            {library.name}
          </Typography>
          {loading && <CircularProgress size="sm" thickness={2} />}
        </Box>

        {isExpanded && (
          <Box sx={{ pl: 0.5 }}>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 1,
                  pl: 3,
                }}
              >
                <CircularProgress size="sm" />
                <Typography level="body-xs" color="neutral">
                  Loading folders...
                </Typography>
              </Box>
            ) : error ? (
              <Alert color="danger" variant="soft" sx={{ mx: 1, mb: 0.5 }}>
                <Typography level="body-xs">{error}</Typography>
              </Alert>
            ) : visibleRoots.length === 0 && searchQuery ? (
              <Typography
                level="body-xs"
                color="neutral"
                sx={{ pl: 3, py: 0.5 }}
              >
                No folders match "{searchQuery}"
              </Typography>
            ) : visibleRoots.length === 0 ? (
              <Typography
                level="body-xs"
                color="neutral"
                sx={{ pl: 3, py: 0.5 }}
              >
                No subfolders — the entire library will be synced.
              </Typography>
            ) : (
              visibleRoots.map((node) => renderFolderNode(node, 1))
            )}
          </Box>
        )}
      </Box>
    );
  };

  const selectedCount = selectedFolders.size;
  const visibleSections = Array.from(librarySections.values()).filter((s) =>
    selectedLibraryIds.has(s.library.drive_id),
  );

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          minWidth: { xs: "90vw", sm: "680px", md: "760px" },
          maxWidth: "90vw",
          minHeight: { xs: "80vh", sm: "600px" },
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DialogTitle>Select SharePoint Folders</DialogTitle>

        <DialogContent
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {/* Error */}
          {librariesError && (
            <Alert
              color="danger"
              variant="soft"
              endDecorator={
                <IconButton
                  variant="plain"
                  size="sm"
                  color="danger"
                  onClick={() => setLibrariesError(null)}
                >
                  <CloseIcon />
                </IconButton>
              }
            >
              <Typography level="body-sm">{librariesError}</Typography>
            </Alert>
          )}

          {/* Initial loading */}
          {librariesLoading && allLibraries.length === 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                py: 6,
              }}
            >
              <CircularProgress size="lg" />
              <Typography level="title-md">
                Connecting to SharePoint...
              </Typography>
              <Typography level="body-sm" color="neutral" textAlign="center">
                Fetching document libraries. This may take a few seconds.
              </Typography>
            </Box>
          )}

          {allLibraries.length > 0 && (
            <>
              {/* Select Libraries */}
              <Box>
                <Typography
                  level="body-xs"
                  fontWeight="lg"
                  sx={{
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "neutral.500",
                  }}
                >
                  Select libraries
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.75}
                  flexWrap="wrap"
                  useFlexGap
                >
                  {allLibraries.map((lib) => {
                    const isSelected = selectedLibraryIds.has(lib.drive_id);
                    return (
                      <Chip
                        key={lib.drive_id}
                        size="sm"
                        variant={isSelected ? "solid" : "outlined"}
                        color={isSelected ? "primary" : "neutral"}
                        onClick={() => toggleLibrary(lib.drive_id)}
                        sx={{ cursor: "pointer" }}
                      >
                        {lib.name}
                      </Chip>
                    );
                  })}
                </Stack>
              </Box>

              {/* Search */}
              <Input
                size="sm"
                placeholder="Search folders..."
                startDecorator={<SearchIcon fontSize="small" />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                endDecorator={
                  searchQuery ? (
                    <IconButton
                      size="sm"
                      variant="plain"
                      onClick={() => setSearchQuery("")}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ) : null
                }
              />

              {/* Browse Folders Tree */}
              <Box
                sx={{
                  flex: 1,
                  overflow: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "sm",
                  p: 0.75,
                  backgroundColor: "background.surface",
                }}
              >
                {selectedLibraryIds.size === 0 ? (
                  <Typography
                    level="body-sm"
                    color="neutral"
                    sx={{ textAlign: "center", py: 4 }}
                  >
                    Select at least one library above to browse folders.
                  </Typography>
                ) : (
                  <Stack spacing={0.5}>
                    {visibleSections.map((section) =>
                      renderLibrarySection(section),
                    )}
                  </Stack>
                )}
              </Box>

              {/* Legend */}
              <Stack direction="row" spacing={2} sx={{ px: 0.5 }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <FolderIcon sx={{ fontSize: 14, color: "primary.500" }} />
                  <Typography level="body-xs" color="neutral">
                    Selected
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <FolderIcon sx={{ fontSize: 14, color: "neutral.400" }} />
                  <Typography level="body-xs" color="neutral">
                    Available
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      border: "1.5px solid",
                      borderColor: "primary.300",
                      borderRadius: 2,
                      bgcolor: "primary.softBg",
                    }}
                  />
                  <Typography level="body-xs" color="neutral">
                    Partial selection
                  </Typography>
                </Stack>
              </Stack>

              {/* Selected Folders Summary */}
              {selectedCount > 0 && (
                <Box>
                  <Typography
                    level="body-xs"
                    fontWeight="lg"
                    sx={{
                      mb: 0.5,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: "neutral.500",
                    }}
                  >
                    {selectedCount} {selectedCount === 1 ? "folder" : "folders"}{" "}
                    selected
                  </Typography>
                  <Stack spacing={0.5}>
                    {Array.from(selectedFolders.entries()).map(([key, sel]) => (
                      <Box
                        key={key}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          py: 0.5,
                          px: 1,
                          backgroundColor: "background.level1",
                          borderRadius: "sm",
                        }}
                      >
                        <FolderIcon
                          sx={{
                            fontSize: 14,
                            color: "primary.500",
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          level="body-xs"
                          sx={{ flex: 1, minWidth: 0 }}
                          noWrap
                        >
                          {sel.path || sel.display_name}
                        </Typography>
                        {sel.library_name && (
                          <Typography
                            level="body-xs"
                            color="neutral"
                            sx={{ flexShrink: 0 }}
                          >
                            {sel.library_name}
                          </Typography>
                        )}
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="danger"
                          onClick={() => handleRemoveSelection(key)}
                        >
                          <RemoveCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Include Subfolders */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Checkbox
                  size="sm"
                  checked={includeSubfolders}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setIncludeSubfolders(next);
                    setSelectedFolders((prev) => {
                      const newMap = new Map(prev);
                      newMap.forEach((sel, key) =>
                        newMap.set(key, { ...sel, recursive: next }),
                      );
                      return newMap;
                    });
                  }}
                />
                <Typography level="body-sm">Include subfolders</Typography>
              </Box>
            </>
          )}
        </DialogContent>

        <Stack
          direction="row"
          spacing={1}
          sx={{ justifyContent: "flex-end", pt: 2 }}
        >
          <Button variant="outlined" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="solid" color="primary" onClick={handleConfirm}>
            {selectedCount > 0 ? `Confirm (${selectedCount})` : "Sync All"}
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export default SharepointFolderPicker;
